import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Edit, Trash } from 'react-feather'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
} from '@material-ui/core'
import { v4 as uuidv4 } from 'uuid'
import { sortBy } from 'lodash'
import { UserGroupFragment, useUserGroupsQuery } from '../../generated/graphql'
import { useUserGroups } from '../../user-group/use-user-groups'
import { Loader } from '../common/Loader'
import { FormValues, UserGroupDialog } from './UserGroupDialog'
import { useAlert } from '../alert/AlertProvider'
import { Icon } from '../common/Icon'
import { UserGroupUserTable } from './UserGroupUserTable'

export function UserGroupsPage(): React.ReactElement {
  const { t } = useTranslation()
  const { alert } = useAlert()
  const { data } = useUserGroupsQuery()
  const { createUserGroup, updateUserGroup, deleteUserGroup } = useUserGroups()
  const [isCreatingUserGroup, setIsCreatingUserGroup] = useState(false)
  const [editingUserGroup, setEditingUserGroup] = useState<
    UserGroupFragment | undefined
  >()

  const userGroups = data?.userGroups

  const onCreateUserGroup = ({ name, userIds }: FormValues) => {
    void createUserGroup({ id: uuidv4(), name, userIds })
    setIsCreatingUserGroup(false)
  }

  const onEditUserGroup = ({ name, userIds }: FormValues) => {
    if (!editingUserGroup) {
      return
    }

    void updateUserGroup(editingUserGroup.id, { name, userIds })
    setEditingUserGroup(undefined)
  }

  const onDeleteUserGroup = (userGroup: UserGroupFragment) => {
    alert(
      t('userGroups.deleteDialog.action'),
      t('userGroups.deleteDialog.message', { name: userGroup.name }),
      [
        { title: t('common.cancel') },
        {
          title: t('userGroups.deleteDialog.action'),
          color: 'secondary',
          onPress: async () => {
            await deleteUserGroup(userGroup.id)
          },
        },
      ],
    )
  }

  if (!userGroups) {
    return <Loader />
  }

  const sortedUserGroups = sortBy(userGroups, (userGroup) =>
    userGroup.name.toLocaleLowerCase(),
  )

  return (
    <Box>
      <Helmet title={t('userGroups.title')} />

      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h3">{t('userGroups.title')}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setIsCreatingUserGroup(true)}
        >
          {t('userGroups.create')}
        </Button>
      </Box>

      <Box component={Paper} my={2}>
        {sortedUserGroups.map((userGroup) => (
          <Accordion key={userGroup.id}>
            <AccordionSummary expandIcon={<ChevronDown />}>
              <Typography variant="h6">
                {t('userGroups.name', {
                  name: userGroup.name,
                  count: userGroup.users.length,
                })}
              </Typography>
              <Box flexGrow={1} />
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation()
                  setEditingUserGroup(userGroup)
                }}
              >
                <Icon component={Edit} size="small" />
              </IconButton>
              <Box mr={1} />
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation()
                  onDeleteUserGroup(userGroup)
                }}
              >
                <Icon component={Trash} size="small" />
              </IconButton>
            </AccordionSummary>

            <AccordionDetails>
              <UserGroupUserTable users={userGroup.users} />
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      <UserGroupDialog
        isOpen={isCreatingUserGroup}
        onSubmit={onCreateUserGroup}
        onClose={() => setIsCreatingUserGroup(false)}
      />

      <UserGroupDialog
        defaultValues={{
          name: editingUserGroup?.name,
          userIds: editingUserGroup?.users?.map((user) => user.id),
        }}
        isOpen={editingUserGroup !== undefined}
        onSubmit={onEditUserGroup}
        onClose={() => setEditingUserGroup(undefined)}
      />
    </Box>
  )
}
